import React, { useState, useEffect } from 'react';
import { hospitalUsersApi, branchesApi, rolesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Users, Plus, PencilSimple, Power, Trash, ShieldCheck, UserCircle, Key } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const HOSPITAL_ROLES = [
  { value: 'hospital_admin', label: 'Hospital Admin', color: 'bg-blue-100 text-blue-800', desc: 'Full hospital access' },
  { value: 'claim_manager', label: 'Claim Manager', color: 'bg-green-100 text-green-800', desc: 'Claims, Pre-auth, Denials, AR' },
  { value: 'billing_executive', label: 'Billing Executive', color: 'bg-orange-100 text-orange-800', desc: 'Billing, Docs, Payments' },
  { value: 'tpa_desk', label: 'TPA Desk', color: 'bg-yellow-100 text-yellow-800', desc: 'TPA/Insurance communication' },
  { value: 'ar_executive', label: 'AR Executive', color: 'bg-pink-100 text-pink-800', desc: 'AR follow-ups & collections' },
  { value: 'finance_manager', label: 'Finance Manager', color: 'bg-indigo-100 text-indigo-800', desc: 'Payments, Reports, Dashboard' },
  { value: 'management_viewer', label: 'Management Viewer', color: 'bg-teal-100 text-teal-800', desc: 'Read-only: Dashboard, Reports' },
  { value: 'reception_user', label: 'Reception User', color: 'bg-cyan-100 text-cyan-800', desc: 'Patient registration, claims' },
];

const emptyForm = { email: '', password: '', name: '', role: 'billing_executive', roles: ['billing_executive'], branch_id: '' };

const HospitalUsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({ name: '', roles: [], branch_id: '' });
  const [resetDialog, setResetDialog] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        hospitalUsersApi.list(),
        branchesApi.list(),
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (!form.roles || form.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      const payload = { ...form, role: form.roles[0] };
      if (!payload.branch_id) delete payload.branch_id;
      await hospitalUsersApi.create(payload);
      const roleLabels = form.roles.map(r => HOSPITAL_ROLES.find(x => x.value === r)?.label || r).join(', ');
      toast.success(`User "${form.name}" created with roles: ${roleLabels}`);
      setDialogOpen(false);
      setForm({ ...emptyForm });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    if (!editDialog) return;
    if (!editForm.roles || editForm.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      const update = {};
      if (editForm.name) update.name = editForm.name;
      update.roles = editForm.roles;
      update.role = editForm.roles[0];
      if (editForm.branch_id) update.branch_id = editForm.branch_id === 'none' ? null : editForm.branch_id;
      await hospitalUsersApi.update(editDialog.id, update);
      toast.success('User updated');
      setEditDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleToggle = async (u) => {
    try {
      const res = await hospitalUsersApi.toggle(u.id);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove "${u.name}" from this hospital? This cannot be undone.`)) return;
    try {
      await hospitalUsersApi.delete(u.id);
      toast.success(`User "${u.name}" removed`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog) return;
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await hospitalUsersApi.resetPassword(resetDialog.id, { new_password: resetPassword });
      toast.success(`Password reset for ${resetDialog.name}`);
      setResetDialog(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getRoleBadges = (roles) => {
    const roleList = roles && roles.length > 0 ? roles : [];
    if (roleList.length === 0) return <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">No role</Badge>;
    return (
      <div className="flex flex-wrap gap-1">
        {roleList.map(role => {
          const r = HOSPITAL_ROLES.find(x => x.value === role);
          return r ? <Badge key={role} className={`${r.color} border-0 text-[10px]`}>{r.label}</Badge>
            : <Badge key={role} className="bg-slate-100 text-slate-600 border-0 text-[10px]">{role}</Badge>;
        })}
      </div>
    );
  };

  const toggleRole = (roleValue, formSetter) => {
    formSetter(prev => {
      const current = prev.roles || [];
      const newRoles = current.includes(roleValue)
        ? current.filter(r => r !== roleValue)
        : [...current, roleValue];
      return { ...prev, roles: newRoles };
    });
  };

  const getBranchName = (branchId) => {
    const b = branches.find(x => x.id === branchId);
    return b?.name || '-';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="hospital-users-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="hospital-users-title">Team Members</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your hospital's users and access permissions</p>
        </div>
        <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2" onClick={() => setDialogOpen(true)} data-testid="add-user-btn">
          <Plus className="h-4 w-4" /> Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border border-slate-200"><CardContent className="py-3 text-center">
          <p className="text-xs text-slate-500 font-semibold">Total Users</p>
          <p className="text-2xl font-bold text-slate-800 font-['Outfit']">{users.length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="py-3 text-center">
          <p className="text-xs text-emerald-600 font-semibold">Active</p>
          <p className="text-2xl font-bold text-emerald-700 font-['Outfit']">{users.filter(u => u.is_active).length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="py-3 text-center">
          <p className="text-xs text-red-600 font-semibold">Inactive</p>
          <p className="text-2xl font-bold text-red-600 font-['Outfit']">{users.filter(u => !u.is_active).length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="py-3 text-center">
          <p className="text-xs text-slate-500 font-semibold">Roles Used</p>
          <p className="text-2xl font-bold text-slate-800 font-['Outfit']">{new Set(users.map(u => u.role)).size}</p>
        </CardContent></Card>
      </div>

      {/* Users Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="users-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Branch</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-sky-50/50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 flex items-center justify-center text-white text-sm font-semibold">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{u.name}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadges(u.roles)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{getBranchName(u.branch_id)}</TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="hover:bg-sky-50 text-sky-600 h-8 w-8 p-0"
                          onClick={() => { setEditDialog(u); setEditForm({ name: u.name, roles: u.roles && u.roles.length > 0 ? u.roles : [u.role], branch_id: u.branch_id || '' }); }}
                          data-testid={`edit-user-${u.id}`}><PencilSimple className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="hover:bg-amber-50 text-amber-600 h-8 w-8 p-0"
                          onClick={() => { setResetDialog(u); setResetPassword(''); }}
                          data-testid={`reset-pwd-${u.id}`} title="Reset Password"><Key className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm"
                          className={`h-8 w-8 p-0 ${u.is_active ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                          onClick={() => handleToggle(u)}
                          data-testid={`toggle-user-${u.id}`}><Power className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="hover:bg-red-50 text-red-500 h-8 w-8 p-0"
                          onClick={() => handleDelete(u)}
                          data-testid={`delete-user-${u.id}`}><Trash className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No users yet. Add your first team member.</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Outfit']">Add New User</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Full Name *</Label>
              <Input placeholder="John Doe" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="user-name-input" /></div>
            <div><Label>Email *</Label>
              <Input type="email" placeholder="john@hospital.com" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="user-email-input" /></div>
            <div><Label>Password *</Label>
              <Input type="password" placeholder="Min 6 characters" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} data-testid="user-password-input" /></div>
            <div><Label>Roles *</Label>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="user-role-select">
                {HOSPITAL_ROLES.map(r => (
                  <label key={r.value} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${form.roles?.includes(r.value) ? 'bg-sky-50 border border-sky-200' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={form.roles?.includes(r.value) || false}
                      onChange={() => toggleRole(r.value, setForm)}
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-800">{r.label}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{r.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
              {form.roles?.length > 0 && <p className="text-[10px] text-sky-600 mt-1">{form.roles.length} role{form.roles.length > 1 ? 's' : ''} selected</p>}
            </div>
            {branches.length > 0 && (
              <div><Label>Branch (Optional)</Label>
                <Select value={form.branch_id} onValueChange={v => setForm(f => ({...f, branch_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="No branch assigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No branch</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={handleCreate} data-testid="submit-user-btn">
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit User: {editDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} /></div>
            <div><Label>Roles *</Label>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="edit-role-select">
                {HOSPITAL_ROLES.map(r => (
                  <label key={r.value} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${editForm.roles?.includes(r.value) ? 'bg-sky-50 border border-sky-200' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={editForm.roles?.includes(r.value) || false}
                      onChange={() => toggleRole(r.value, setEditForm)}
                      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-800">{r.label}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{r.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
              {editForm.roles?.length > 0 && <p className="text-[10px] text-sky-600 mt-1">{editForm.roles.length} role{editForm.roles.length > 1 ? 's' : ''} selected</p>}
            </div>
            {branches.length > 0 && (
              <div><Label>Branch</Label>
                <Select value={editForm.branch_id || 'none'} onValueChange={v => setEditForm(f => ({...f, branch_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="No branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No branch</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={handleUpdate} data-testid="update-user-btn">
              Update User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={() => setResetDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-['Outfit']">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Set new password for <strong>{resetDialog?.name}</strong> ({resetDialog?.email})</p>
            <div>
              <Label>New Password</Label>
              <Input type="password" placeholder="Min 6 characters" value={resetPassword}
                onChange={e => setResetPassword(e.target.value)} data-testid="reset-password-input" />
            </div>
            <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white" onClick={handleResetPassword} data-testid="confirm-reset-pwd-btn">
              Reset Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HospitalUsersPage;
